import React, { useState, useEffect, useReducer } from "react";
import openSocket from "../../services/socket-io";

import {
  Hidden,
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  InputAdornment,
  TextField,
} from "@material-ui/core";
import { Edit, DeleteOutline, AddBox, Search } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import DbServerModal from "../../components/DbServerModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
  if (action.type === "LOAD") {
    const dbServers = action.payload;
    const newDbServers = [];

    dbServers.forEach((dbServer) => {
      const index = state.findIndex((q) => q.id === dbServer.id);
      if (index !== -1) {
        state[index] = dbServer;
      } else {
        newDbServers.push(dbServer);
      }
    });

    return [...state, ...newDbServers];
  }

  if (action.type === "UPDATE") {
    const dbServer = action.payload;
    const index = state.findIndex((q) => q.id === dbServer.id);

    if (index !== -1) {
      state[index] = dbServer;
      return [...state];
    }

    return [dbServer, ...state];
  }

  if (action.type === "DELETE") {
    const dbServerId = action.payload;
    const index = state.findIndex((q) => q.id === dbServerId);
    if (index !== -1) {
      state.splice(index, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const DbServers = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [dbServers, dispatch] = useReducer(reducer, []);
  const [selectedDbServer, setSelectedDbServer] = useState(null);
  const [dbServerModalOpen, setDbServerModalOpen] = useState(false);
  const [deletingDbServer, setDeletingDbServer] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchDbServers = async () => {
        try {
          const { data } = await api.get("/dbServers/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD", payload: data.dbServers });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchDbServers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("dbServer", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE", payload: data.dbServer });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE", payload: +data.dbServerId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenModal = () => {
    setSelectedDbServer(null);
    setDbServerModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedDbServer(null);
    setDbServerModalOpen(false);
  };

  const handleEdit = (dbServer) => {
    setSelectedDbServer(dbServer);
    setDbServerModalOpen(true);
  };

  const handleDelete = async (dbServerId) => {
    try {
      await api.delete(`/dbServers/${dbServerId}`);
      toast.success(i18n.t("global.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingDbServer(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingDbServer && `${i18n.t("global.deleteTitle")} ${deletingDbServer.name}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deletingDbServer.id)}
      >
        {i18n.t("global.deleteMessage")}
      </ConfirmationModal>
      <DbServerModal
        open={dbServerModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="form-dialog-title"
        dbServerId={selectedDbServer && selectedDbServer.id}
      />

      <MainHeader>
        <Title>DB Servers</Title>
        <MainHeaderButtonsWrapper>
          <Hidden mdDown>
            <TextField
              placeholder={i18n.t("global.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Hidden>
          <Button variant="contained" color="primary" onClick={handleOpenModal}>
            <AddBox /> <Hidden mdDown>{i18n.t("global.buttons.add")}</Hidden>
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Hidden lgUp>
        <TextField
          placeholder={i18n.t("global.searchPlaceholder")}
          type="search"
          value={searchParam}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search style={{ color: "gray" }} />
              </InputAdornment>
            ),
          }}
        />
        <br />
      </Hidden>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">Nome</TableCell>
              <TableCell align="center">Endereço</TableCell>
              <TableCell align="center">Dialeto</TableCell>
              <TableCell align="center">Timezone</TableCell>
              <TableCell align="center">Porta</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">{i18n.t("global.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {dbServers.map((dbServer) => (
                <TableRow key={dbServer.id}>
                  <TableCell align="center">{dbServer.name}</TableCell>
                  <TableCell align="center">{dbServer.address}</TableCell>
                  <TableCell align="center">{dbServer.dialect}</TableCell>
                  <TableCell align="center">{dbServer.timeZone}</TableCell>
                  <TableCell align="center">{dbServer.port}</TableCell>
                  <TableCell align="center">{dbServer.enabled ? "✅" : "❌"}</TableCell>
                  <TableCell align="center">{dbServer.status ? "✅" : "❌"}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEdit(dbServer)}>
                      <Edit />
                    </IconButton>
                    <IconButton size="small" onClick={() => { setConfirmModalOpen(true); setDeletingDbServer(dbServer); }}>
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={8} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default DbServers;
